/* ------------------------------------------------------------------ *
 * Dotted paths
 *
 * `bounds[].field` is a dotted path (`levels.often.maxPerDay`), so reading and
 * writing one needs a path walk. Written out rather than pulled from lodash:
 * the project has no `@types/lodash`, and these two are simpler than the
 * `require` that would otherwise be needed to dodge that.
 * ------------------------------------------------------------------ */

function get(source: unknown, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>(
      (value, key) =>
        value && typeof value === "object"
          ? (value as Record<string, unknown>)[key]
          : undefined,
      source
    );
}

function set(target: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split(".");
  const last = keys.pop();

  if (!last) return;

  const parent = keys.reduce<Record<string, unknown>>((node, key) => {
    if (typeof node[key] !== "object" || node[key] === null) node[key] = {};

    return node[key] as Record<string, unknown>;
  }, target);

  parent[last] = value;
}

/* ------------------------------------------------------------------ *
 * Shapes
 * ------------------------------------------------------------------ */

/**
 * The range, unit and explanation for one field, shipped by the server.
 *
 * This is the whole reason the form does not hardcode anything: the same table
 * the validator rejects against is the table the inputs are built from, so a
 * bound changed on the server reaches this screen without a dashboard release.
 * Treat it as the source of truth — never a min/max literal in this folder.
 */
export interface NearbyAlertsBound {
  /** Dotted path into the config, e.g. `levels.often.maxPerDay`. */
  field: string;
  min: number;
  max: number;
  /** The compiled app default. Used as the input's placeholder. */
  defaultValue: number;
  unit: string;
  /** False only for `stationarySpeedMps`. */
  integer: boolean;
  description: string;
}

export interface NearbyAlertsSettings {
  /** Stored values with defaults filling any gap — what a client resolves to. */
  config: Record<string, unknown> & { enabled: boolean };
  /** The `nearbyAlertsEnabled` row: the fleet-wide kill switch. */
  killSwitchEnabled: boolean;
  /** `config.enabled && killSwitchEnabled` — what actually reaches a member. */
  effectiveEnabled: boolean;
  /** True when no stored row exists; the app is on compiled defaults. */
  usingDefaults: boolean;
  warnings: Array<string>;
  /** Last write to either row. Stands in for an audit trail, which does not exist. */
  updatedAt?: string | null;
  bounds: Array<NearbyAlertsBound>;
}

/** Dotted path → the raw string in its input. */
export type Draft = Record<string, string>;

/* ------------------------------------------------------------------ *
 * Standing copy
 * ------------------------------------------------------------------ */

/**
 * Config is fetched once per app launch, and the values that steer the OS take
 * hold at the next scan after that. Never imply a save reaches anyone's phone
 * immediately — it is a fast switch, not an instant one, and that is the best
 * any client-side config can do.
 */
export const PROPAGATION_NOTICE =
  "Next app launch, plus one scan interval — up to about 30 minutes after that.";

/**
 * Quiet hours run in the member's own local time on their device, not a server
 * timezone. Needs saying because "21:00–09:00" in an admin panel invites the
 * assumption that it is one global window.
 */
export const QUIET_HOURS_NOTICE =
  "Quiet hours are evaluated in each member's own local time on their device, not one global window.";

/* ------------------------------------------------------------------ *
 * Which bounds this screen edits
 * ------------------------------------------------------------------ */

/**
 * The per-level notification budgets (`levels.occasionally.*`,
 * `levels.often.*`), which this screen deliberately does not edit.
 *
 * Matched on the path prefix rather than by naming the six fields, so adding a
 * fourth budget field on the server does not make one appear here on its own.
 */
export const EXCLUDED_PREFIX = "levels.";

/**
 * The bounds this form renders — everything except the level budgets.
 *
 * Filtering here rather than in the component keeps the draft, the diff and
 * the patch working off the same list: a field excluded from display but left
 * in the draft would be silently included in the next save.
 */
export function formBounds(
  bounds: Array<NearbyAlertsBound>
): Array<NearbyAlertsBound> {
  return bounds.filter((bound) => !bound.field.startsWith(EXCLUDED_PREFIX));
}

/**
 * `businessRadiusMeters` → `Business radius`, with the unit word dropped.
 *
 * Cosmetic only — the unit is rendered beside the input already, so keeping it
 * would say it twice. Driven by the payload's own `unit` rather than a list of
 * known fields, so an unfamiliar field still gets a sensible label instead of
 * being missed.
 */
export function humanizeField(bound: NearbyAlertsBound): string {
  const last = bound.field.split(".").pop() ?? bound.field;
  const words = last.replace(/([A-Z])/g, " $1").trim().split(/\s+/);

  const trimmed =
    words.length > 1 &&
    singular(words[words.length - 1]) === singular(bound.unit)
      ? words.slice(0, -1)
      : words;

  return capitalize(trimmed.join(" ").toLowerCase());
}

/**
 * Enough normalisation to match a field name against its unit.
 *
 * The two use different spellings on purpose-built data: the field is
 * `businessRadiusMeters` while the unit reads `metres`. Only ever affects a
 * label, so being approximate here is free — `m/s` matching nothing and
 * keeping every word is the correct outcome.
 */
function singular(word: string): string {
  return word.toLowerCase().replace(/e?s$/, "").replace(/metre$|meter$/, "metr");
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/* ------------------------------------------------------------------ *
 * Draft <-> config
 * ------------------------------------------------------------------ */

/** Seeds one input per bound from the stored config. */
export function toDraft(
  config: Record<string, unknown>,
  bounds: Array<NearbyAlertsBound>
): Draft {
  const draft: Draft = {};

  bounds.forEach((bound) => {
    const value = get(config, bound.field);
    draft[bound.field] = value === undefined || value === null ? "" : String(value);
  });

  return draft;
}

/** Paths whose input no longer matches the stored config. */
export function changedFields(
  draft: Draft,
  config: Record<string, unknown>,
  bounds: Array<NearbyAlertsBound>
): Array<string> {
  return bounds
    .map((bound) => bound.field)
    .filter((field) => {
      const current = get(config, field);
      const currentText =
        current === undefined || current === null ? "" : String(current);

      // Compared as numbers where both parse, so "1.50" and 1.5 are the same
      // edit and the form does not offer to save a reformat.
      const before = Number(currentText);
      const after = Number(draft[field]);

      if (Number.isFinite(before) && Number.isFinite(after)) {
        return before !== after;
      }

      return currentText !== (draft[field] ?? "");
    });
}

/**
 * Builds the partial input — only what changed, nested to match the schema.
 *
 * Sending the whole config instead would overwrite any field a newer client
 * added that this dashboard does not render, and the server merges patches
 * precisely so that never has to happen.
 */
export function buildPatch(
  draft: Draft,
  config: Record<string, unknown>,
  bounds: Array<NearbyAlertsBound>
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  changedFields(draft, config, bounds).forEach((field) => {
    set(patch, field, Number(draft[field]));
  });

  return patch;
}

/* ------------------------------------------------------------------ *
 * Validation
 * ------------------------------------------------------------------ */

/**
 * Client-side range check, mirroring the server's using the server's own
 * numbers.
 *
 * The server is still the authority and rejects rather than clamps; this only
 * saves a round trip and puts the message next to the input. It deliberately
 * does not implement the cross-field rules (`proximityRadiusMeters` ≥ 2 ×
 * `businessRadiusMeters`) — those are evaluated against the merged result,
 * which only the server has.
 */
export function validateDraft(
  draft: Draft,
  bounds: Array<NearbyAlertsBound>
): Record<string, string> {
  const errors: Record<string, string> = {};

  bounds.forEach((bound) => {
    const raw = (draft[bound.field] ?? "").trim();

    if (!raw) {
      errors[bound.field] = "Required.";
      return;
    }

    const value = Number(raw);

    if (!Number.isFinite(value)) {
      errors[bound.field] = "Must be a number.";
      return;
    }

    if (bound.integer && !Number.isInteger(value)) {
      errors[bound.field] = "Must be a whole number.";
      return;
    }

    if (value < bound.min || value > bound.max) {
      errors[bound.field] = `Must be between ${bound.min} and ${bound.max} ${bound.unit}.`;
    }
  });

  return errors;
}

/* ------------------------------------------------------------------ *
 * Server rejections
 * ------------------------------------------------------------------ */

export interface FieldError {
  field: string;
  message: string;
}

/**
 * Pulls the offending field off a rejection so the message can be attached to
 * the input that caused it.
 *
 * The server puts it in `extensions.field` precisely so the dashboard does not
 * have to show a banner and let the admin hunt for which of fifteen inputs it
 * meant. Returns null when the rejection names no field — a cross-field rule,
 * or anything that is not a validation error.
 */
export function fieldErrorOf(error: unknown): FieldError | null {
  const errors = (
    error as {
      graphQLErrors?: Array<{
        message?: string;
        extensions?: { field?: unknown };
      }>;
    }
  )?.graphQLErrors;

  const first = errors?.[0];
  const field = first?.extensions?.field;

  if (typeof field !== "string" || !field) return null;

  return { field, message: first?.message ?? "Rejected by the server." };
}

